/**
 * GUIHierarchyShadowTab — read-only analytics view of the Phase 2a/3a
 * shadow-mode GUI Hierarchy.
 *
 * Three panels:
 *   1. Agreement matrix — BOTH_FIRE / LIVE_ONLY / HIERARCHY_ONLY / BOTH_BLOCK
 *      counts over a selectable day window.
 *   2. Block-reason histogram — which hierarchy layer is blocking the most.
 *   3. LIVE_ONLY queue — individual signals that the live engine fired but
 *      the hierarchy would have blocked. Includes block_reason + rationale.
 *
 * Everything is read-only. No actions fire a signal, stop a signal, or
 * promote a layer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Shield, AlertTriangle, Activity, FlaskConical, TrendingUp, Target } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGREEMENT_STYLES = {
  BOTH_FIRE: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  BOTH_FIRE_CONFLICT: 'bg-orange-500/20 text-orange-400 border-orange-500/40',
  LIVE_ONLY: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
  HIERARCHY_ONLY: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  BOTH_BLOCK: 'bg-muted text-muted-foreground border-border',
};

const AGREEMENT_DESCRIPTIONS = {
  BOTH_FIRE: 'Both sides agreed — same direction. The gold-standard signal.',
  BOTH_FIRE_CONFLICT: 'Both fired in OPPOSITE directions. Live engine likely misread the tape.',
  LIVE_ONLY: 'Live engine fired; hierarchy would have blocked. Target-reducible false positives.',
  HIERARCHY_ONLY: 'Hierarchy would have fired; live engine was silent. Potential missed signals.',
  BOTH_BLOCK: 'Neither side fired — routine neutral state.',
};

// Fetch wrapper that sends the JWT from localStorage (same pattern as
// SafeguardsTab / ConfluenceDashboard).
async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

const SummaryCards = ({ summary }) => {
  if (!summary) return null;
  const total = summary.total_decisions || 0;
  const live = summary.live_engine_fires || 0;
  const blocked = summary.hierarchy_would_block_of_live_fires || 0;
  const rate = summary.hierarchy_block_rate_of_live_fires || 0;

  // Block rate is expected to be high in the early days of shadow mode —
  // the engine only fires during real moves (so most signals ARE
  // structural chases by definition). A 100% block rate here is noisy
  // but not alarming. Color:
  //   • green if <30%  — hierarchy largely agrees with the live engine
  //   • amber if 30-80% — normal early-shadow disagreement
  //   • red if >80%    — hierarchy is filtering nearly everything; review
  let rateTone = 'text-emerald-400';
  let rateBorder = '';
  if (rate > 0.8) {
    rateTone = 'text-rose-400';
    rateBorder = 'border-rose-500/40';
  } else if (rate > 0.3) {
    rateTone = 'text-amber-400';
    rateBorder = 'border-amber-500/30';
  }

  const cards = [
    {
      label: 'Total decisions',
      value: total,
      helper: `${summary.days}-day window`,
      testid: 'shadow-total-decisions',
    },
    {
      label: 'Live engine fires',
      value: live,
      helper: 'BOTH_FIRE + LIVE_ONLY',
      testid: 'shadow-live-fires',
    },
    {
      label: 'Hierarchy would block',
      value: blocked,
      helper: 'of live engine fires',
      testid: 'shadow-would-block',
    },
    {
      label: 'Block rate',
      value: `${(rate * 100).toFixed(1)}%`,
      helper: rate > 0.8
        ? 'Very high — review block reasons'
        : rate > 0.3
        ? 'Healthy disagreement during shadow'
        : 'Hierarchy & live engine agree',
      testid: 'shadow-block-rate',
      toneClass: rateTone,
      border: rateBorder,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div
          key={c.label}
          data-testid={c.testid}
          className={`bg-surface border border-border p-4 ${c.border || ''}`}
        >
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {c.label}
          </div>
          <div
            className={`text-3xl font-mono mt-2 font-semibold ${
              c.toneClass || 'text-foreground'
            }`}
          >
            {c.value}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{c.helper}</div>
        </div>
      ))}
    </div>
  );
};

const AgreementMatrix = ({ rows }) => {
  if (!rows?.length) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border">
        No shadow decisions yet in the selected window.
      </div>
    );
  }

  // Group rows by symbol for a compact 2-col table.
  const bySymbol = rows.reduce((acc, r) => {
    (acc[r.symbol] ||= []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(bySymbol).map(([symbol, symRows]) => (
        <div key={symbol} className="border border-border">
          <div className="px-4 py-2 bg-surface border-b border-border flex items-center gap-2">
            <Activity className="w-4 h-4 text-accent" />
            <span className="font-heading font-semibold">{symbol}</span>
          </div>
          <div className="divide-y divide-border">
            {symRows.map((r) => (
              <div
                key={`${symbol}-${r.agreement}`}
                className="px-4 py-3 flex items-center justify-between gap-4"
                data-testid={`shadow-agreement-${symbol}-${r.agreement}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className={`${AGREEMENT_STYLES[r.agreement] || ''} text-[10px]`}>
                      {r.agreement}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {AGREEMENT_DESCRIPTIONS[r.agreement] || ''}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                    live score avg: {r.avg_live_score} · hierarchy score avg: {r.avg_hierarchy_score}
                  </div>
                </div>
                <div className="text-2xl font-mono font-semibold text-foreground">
                  {r.count}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const BlockReasonHistogram = ({ rows }) => {
  if (!rows?.length) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border">
        No block decisions recorded yet.
      </div>
    );
  }
  // Aggregate by block_reason (rows may be grouped by symbol too).
  const byReason = rows.reduce((acc, r) => {
    const key = r.block_reason || '(none)';
    acc[key] = (acc[key] || 0) + r.count;
    return acc;
  }, {});
  const aggregated = Object.entries(byReason)
    .map(([block_reason, count]) => ({ block_reason, count }))
    .sort((a, b) => b.count - a.count);
  const max = Math.max(...aggregated.map((r) => r.count));

  return (
    <div className="space-y-2" data-testid="shadow-block-histogram">
      {aggregated.map((r) => {
        const pct = max > 0 ? (r.count / max) * 100 : 0;
        return (
          <div key={r.block_reason} className="flex items-center gap-3">
            <div className="w-60 text-xs font-mono truncate text-muted-foreground">
              {r.block_reason}
            </div>
            <div className="flex-1 bg-muted/30 h-6 relative">
              <div
                className="absolute inset-y-0 left-0 bg-accent/40"
                style={{ width: `${pct}%` }}
              />
              <div className="absolute inset-0 flex items-center px-2 text-xs font-mono">
                {r.count}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const LAYER_STYLES = {
  LAYER1: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
  LAYER2: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  LAYER3: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
  LAYER4: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
};

const LAYER_DESCRIPTIONS = {
  LAYER1: 'Context Gate (IB, day-type, EOD, lunch, VIX)',
  LAYER2: 'Setup Classifier (RB / IB_BREAK / APB / NPOC match)',
  LAYER3: 'Per-setup score threshold',
  LAYER4: 'Location Validator (structural SL/T1/R:R)',
};

/**
 * PerSymbolLayerMatrix — NIFTY vs BANKNIFTY × layer block counts.
 * Answers "at which layer did signals get blocked for each symbol?"
 */
const PerSymbolLayerMatrix = ({ rows }) => {
  if (!rows?.length) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border">
        No per-symbol block data yet — collection is empty or all signals passed.
      </div>
    );
  }

  // Aggregate into { symbol: { LAYER1: {count, live_fires_blocked}, LAYER2: ...} }
  const matrix = {};
  const symbols = new Set();
  for (const r of rows) {
    const sym = r.symbol || '(unknown)';
    const layer = r.layer || 'OTHER';
    symbols.add(sym);
    matrix[sym] ??= {};
    matrix[sym][layer] ??= { count: 0, live_fires_blocked: 0, reasons: [] };
    matrix[sym][layer].count += r.count;
    matrix[sym][layer].live_fires_blocked += r.live_fires_blocked || 0;
    if (r.block_reason) matrix[sym][layer].reasons.push(r.block_reason);
  }

  const layers = ['LAYER1', 'LAYER2', 'LAYER3', 'LAYER4'];
  const sortedSymbols = Array.from(symbols).sort();

  return (
    <div className="overflow-x-auto border border-border" data-testid="shadow-layer-matrix">
      <table className="w-full text-xs">
        <thead className="bg-surface border-b border-border">
          <tr className="text-left text-muted-foreground uppercase tracking-wider">
            <th className="px-3 py-2">Symbol</th>
            {layers.map((l) => (
              <th key={l} className="px-3 py-2 text-center">
                <div className="flex flex-col items-center gap-1">
                  <span className={`px-2 py-0.5 border text-[10px] font-mono ${LAYER_STYLES[l]}`}>
                    {l}
                  </span>
                  <span className="font-normal text-[10px] normal-case tracking-normal text-muted-foreground">
                    {LAYER_DESCRIPTIONS[l]}
                  </span>
                </div>
              </th>
            ))}
            <th className="px-3 py-2 text-center">Total blocks</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedSymbols.map((sym) => {
            const row = matrix[sym] || {};
            const total = layers.reduce((a, l) => a + (row[l]?.count || 0), 0);
            return (
              <tr key={sym} className="hover:bg-muted/20" data-testid={`shadow-layer-row-${sym}`}>
                <td className="px-3 py-3 font-semibold">{sym}</td>
                {layers.map((l) => {
                  const cell = row[l];
                  if (!cell) {
                    return (
                      <td key={l} className="px-3 py-3 text-center text-muted-foreground font-mono">
                        —
                      </td>
                    );
                  }
                  const reasonPreview = cell.reasons.slice(0, 3).join('\n');
                  return (
                    <td
                      key={l}
                      className="px-3 py-3 text-center font-mono"
                      title={reasonPreview}
                      data-testid={`shadow-layer-cell-${sym}-${l}`}
                    >
                      <div className="text-lg font-semibold text-foreground">
                        {cell.count.toLocaleString()}
                      </div>
                      {cell.live_fires_blocked > 0 && (
                        <div className="text-[10px] text-rose-400 mt-0.5">
                          {cell.live_fires_blocked} live fires
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-center font-mono font-semibold text-foreground">
                  {total.toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 bg-surface/50 border-t border-border text-[10px] text-muted-foreground">
        Hover a cell to preview the exact block_reason strings · red annotation =
        number of live-engine fires that layer would have blocked (the signal we
        care about most).
      </div>
    </div>
  );
};

/**
 * HypotheticalPnLPanel — the "would blocking have saved points?" answer.
 * For each (symbol, would_block) bucket it shows resolved trades, hit rate,
 * avg PnL, and net points. The big KPI at top is `points_saved_by_hierarchy`
 * — the sum of losses on live-engine fires the hierarchy would have blocked.
 */
const HypotheticalPnLPanel = ({ data }) => {
  if (!data) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border">
        Loading hypothetical PnL…
      </div>
    );
  }
  const rows = data.rows || [];
  const saved = data.points_saved_by_hierarchy || 0;

  // Split rows into "would-block" (the saved-loss candidates) and the rest.
  const blockedRows = rows.filter((r) => r.hierarchy_would_block);
  const passedRows = rows.filter((r) => !r.hierarchy_would_block);

  const netBlocked = blockedRows.reduce((a, r) => a + (r.net_pnl_pts || 0), 0);
  const totalBlocked = blockedRows.reduce((a, r) => a + (r.count || 0), 0);
  const totalResolvedBlocked = blockedRows.reduce(
    (a, r) => a + (r.resolved || 0),
    0,
  );

  if (!rows.length) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border">
        No resolved outcomes yet. The tracker polls every 60s and fills in
        t+5/15/30/60m price samples per decision. Come back in ~10 minutes.
      </div>
    );
  }

  const kpiColor =
    netBlocked < -0 ? 'text-emerald-400' : netBlocked > 0 ? 'text-rose-400' : 'text-foreground';

  return (
    <div className="space-y-4" data-testid="shadow-pnl-panel">
      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          className="bg-surface border border-border p-4"
          data-testid="shadow-pnl-saved"
        >
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Points saved by hierarchy
          </div>
          <div className={`text-3xl font-mono mt-2 font-semibold ${kpiColor}`}>
            {saved > 0 ? '+' : ''}
            {saved.toFixed(1)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            sum of losses on blocked live fires
          </div>
        </div>

        <div className="bg-surface border border-border p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Blocked live fires
          </div>
          <div className="text-3xl font-mono mt-2 font-semibold text-foreground">
            {totalBlocked}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {totalResolvedBlocked} resolved · {totalBlocked - totalResolvedBlocked} pending
          </div>
        </div>

        <div className="bg-surface border border-border p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Net PnL (blocked trades if they had fired)
          </div>
          <div
            className={`text-3xl font-mono mt-2 font-semibold ${
              netBlocked >= 0 ? 'text-rose-400' : 'text-emerald-400'
            }`}
          >
            {netBlocked > 0 ? '+' : ''}
            {netBlocked.toFixed(1)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            negative = hierarchy was right · positive = it cost us
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div className="overflow-x-auto border border-border" data-testid="shadow-pnl-table">
        <table className="w-full text-xs">
          <thead className="bg-surface border-b border-border">
            <tr className="text-left text-muted-foreground uppercase tracking-wider">
              <th className="px-3 py-2">Symbol</th>
              <th className="px-3 py-2">Bucket</th>
              <th className="px-3 py-2 text-right">Count</th>
              <th className="px-3 py-2 text-right">Resolved</th>
              <th className="px-3 py-2 text-right">Hit T1</th>
              <th className="px-3 py-2 text-right">Hit SL</th>
              <th className="px-3 py-2 text-right">Open</th>
              <th className="px-3 py-2 text-right">Avg PnL (pts)</th>
              <th className="px-3 py-2 text-right">Net PnL (pts)</th>
              <th className="px-3 py-2 text-right">Avg MAE / MFE</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {[...blockedRows, ...passedRows].map((r, i) => {
              const bucket = r.hierarchy_would_block ? 'WOULD_BLOCK' : 'WOULD_FIRE';
              const bucketStyle = r.hierarchy_would_block
                ? 'bg-rose-500/10 text-rose-400 border-rose-500/30'
                : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
              return (
                <tr key={i} className="hover:bg-muted/20">
                  <td className="px-3 py-2 font-semibold">{r.symbol}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 border text-[10px] font-mono ${bucketStyle}`}>
                      {bucket}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{r.count}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.resolved}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-400">{r.hits_t1}</td>
                  <td className="px-3 py-2 text-right font-mono text-rose-400">{r.hits_sl}</td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {r.open_or_flat}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono ${
                      r.avg_pnl_pts < 0 ? 'text-rose-400' : 'text-emerald-400'
                    }`}
                  >
                    {r.avg_pnl_pts > 0 ? '+' : ''}
                    {r.avg_pnl_pts.toFixed(1)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono font-semibold ${
                      r.net_pnl_pts < 0 ? 'text-rose-400' : 'text-emerald-400'
                    }`}
                  >
                    {r.net_pnl_pts > 0 ? '+' : ''}
                    {r.net_pnl_pts.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {r.avg_mae_pts.toFixed(0)} / {r.avg_mfe_pts.toFixed(0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-3 py-2 bg-surface/50 border-t border-border text-[10px] text-muted-foreground">
          <strong>Read this as:</strong> If the net PnL on <em>WOULD_BLOCK</em> rows
          is significantly negative, the hierarchy correctly caught losing trades
          — promote the relevant layer. Fixed SL/T1 assumed: NIFTY 30/60, BNF 150/300.
        </div>
      </div>
    </div>
  );
};

/**
 * HierarchySignalsPanel — the "which trades would the hierarchy have taken?"
 * view. Deduped: repeated detections of the same setup within a 15-min
 * window collapse into one row with a `count`. Each row shows the entry,
 * SL, T1, R:R, full rationale, and whether the live engine also fired.
 */
const SETUP_COLORS = {
  APB: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
  RB: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  RS: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
  IB_BREAK: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
  IS_BREAK: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
  NPOC: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
};

const HierarchySignalsPanel = ({ data, loading }) => {
  if (loading && !data) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border">
        Loading hierarchy signals…
      </div>
    );
  }
  if (!data || !data.rows?.length) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border">
        No hierarchy fires in this window. The hierarchy has been silent.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="hierarchy-signals-panel">
      <div className="text-xs text-muted-foreground font-mono">
        {data.unique_signals} unique signal{data.unique_signals === 1 ? '' : 's'} from {data.raw_fires} raw fires
        (deduped {data.dedup_window_min}-min window)
      </div>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-xs">
          <thead className="bg-surface border-b border-border">
            <tr className="text-left text-muted-foreground uppercase tracking-wider">
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Symbol</th>
              <th className="px-3 py-2">Setup</th>
              <th className="px-3 py-2">Dir</th>
              <th className="px-3 py-2 text-right">Entry</th>
              <th className="px-3 py-2 text-right">SL</th>
              <th className="px-3 py-2 text-right">T1</th>
              <th className="px-3 py-2 text-right">R:R</th>
              <th className="px-3 py-2 text-right">Fires</th>
              <th className="px-3 py-2">Live agreement</th>
              <th className="px-3 py-2">Rationale</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.rows.map((r, i) => {
              const setupStyle =
                SETUP_COLORS[r.setup_type] || 'bg-muted text-muted-foreground border-border';
              const dirStyle =
                r.direction === 'LONG'
                  ? 'text-emerald-400'
                  : r.direction === 'SHORT'
                  ? 'text-rose-400'
                  : 'text-muted-foreground';
              const liveTag = (() => {
                if (r.live_agreement === 'BOTH_FIRE') {
                  return (
                    <span className="px-2 py-0.5 border text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                      LIVE AGREED
                    </span>
                  );
                }
                if (r.live_agreement === 'BOTH_FIRE_CONFLICT') {
                  return (
                    <span
                      className="px-2 py-0.5 border text-[10px] font-mono bg-orange-500/20 text-orange-400 border-orange-500/40"
                      title={`Live fired ${r.live_direction} · score ${r.live_score}`}
                    >
                      LIVE CONFLICT ({r.live_direction})
                    </span>
                  );
                }
                return (
                  <span className="px-2 py-0.5 border text-[10px] font-mono bg-amber-500/10 text-amber-400 border-amber-500/30">
                    LIVE SILENT
                  </span>
                );
              })();
              return (
                <tr
                  key={i}
                  className="hover:bg-muted/20"
                  data-testid={`hierarchy-signal-${r.symbol}-${r.first_seen_ts}`}
                >
                  <td className="px-3 py-2 font-mono whitespace-nowrap">
                    {r.first_seen_ts?.slice(11, 19) || '—'}
                  </td>
                  <td className="px-3 py-2 font-semibold">{r.symbol}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 border text-[10px] font-mono ${setupStyle}`}>
                      {r.setup_type}
                    </span>
                  </td>
                  <td className={`px-3 py-2 font-mono font-semibold ${dirStyle}`}>
                    {r.direction}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.entry_price?.toFixed(1) || '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-rose-400">
                    {r.sl_price?.toFixed(1) || '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-400">
                    {r.t1_price?.toFixed(1) || '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.risk_reward ? `${r.risk_reward.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    ×{r.count}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{liveTag}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground max-w-md">
                    {(r.rationale || []).join(' · ') || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-3 py-2 bg-surface/50 border-t border-border text-[10px] text-muted-foreground">
          <strong>Read this as:</strong> These are trades the hierarchy would
          have entered. <em>Fires</em> = how many ticks the setup stayed
          valid (not extra signals — one trade). <em>LIVE CONFLICT</em> means
          live engine fired OPPOSITE direction — critical review case.
          Deploying live would trigger <strong>one</strong> order per row,
          not per fire.
        </div>
      </div>
    </div>
  );
};

const LiveOnlyBlocksTable = ({ rows }) => {
  if (!rows?.length) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 border border-dashed border-border">
        No LIVE_ONLY blocks — the hierarchy and live engine agree on every fire.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-border" data-testid="shadow-live-only-table">
      <table className="w-full text-xs">
        <thead className="bg-surface border-b border-border">
          <tr className="text-left text-muted-foreground uppercase tracking-wider">
            <th className="px-3 py-2">Time</th>
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2">Live state</th>
            <th className="px-3 py-2">Dir</th>
            <th className="px-3 py-2 text-right">Score</th>
            <th className="px-3 py-2">Block reason</th>
            <th className="px-3 py-2">Gate</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, idx) => (
            <tr key={idx} className="hover:bg-muted/20">
              <td className="px-3 py-2 font-mono">
                {r.timestamp?.slice(11, 19) || '—'}
              </td>
              <td className="px-3 py-2 font-semibold">{r.symbol}</td>
              <td className="px-3 py-2">
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[10px]">
                  {r.live_engine_decision?.signal_state || '—'}
                </Badge>
              </td>
              <td className="px-3 py-2 font-mono">
                {r.live_engine_decision?.direction || '—'}
              </td>
              <td className="px-3 py-2 text-right font-mono">
                {r.live_engine_decision?.total_score?.toFixed?.(1) ?? '—'}
              </td>
              <td className="px-3 py-2 font-mono text-rose-400">
                {r.hierarchy_decision?.block_reason || '—'}
              </td>
              <td className="px-3 py-2 font-mono text-muted-foreground">
                {r.hierarchy_decision?.context_gate?.reason || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Session 146 — Freshness / window strip.
 *
 * Answers the user's question "how do I know the data is for today?"
 * by explicitly showing:
 *   • the calendar window (from → to, IST)
 *   • the last decision timestamp (age in seconds/minutes)
 *   • count of decisions that landed today
 *
 * The latest-decision age pill goes red if nothing has landed in the
 * last 10 minutes during market hours — a strong hint the feed is down.
 */
const DataFreshnessStrip = ({ summary, todayOnly, loading }) => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  if (!summary) {
    return (
      <div
        className="text-xs text-muted-foreground font-mono px-3 py-2 border border-border/60 bg-surface/40 flex items-center gap-2"
        data-testid="shadow-freshness-strip"
      >
        <Activity className="w-3 h-3" />
        {loading ? 'Loading window…' : 'Waiting for shadow data…'}
      </div>
    );
  }

  const fromDate = summary.window_from_date || summary.cutoff_date;
  const toDate = summary.window_to_date;
  const latest = summary.latest_decision_at;
  const latestMs = latest ? new Date(latest).getTime() : null;
  const ageSec = latestMs ? Math.max(0, Math.round((nowMs - latestMs) / 1000)) : null;

  let ageLabel = '—';
  let ageTone = 'text-muted-foreground';
  if (ageSec == null) {
    ageLabel = 'no decisions yet';
    ageTone = 'text-rose-400';
  } else if (ageSec < 60) {
    ageLabel = `${ageSec}s ago`;
    ageTone = 'text-emerald-400';
  } else if (ageSec < 3600) {
    ageLabel = `${Math.round(ageSec / 60)}m ago`;
    ageTone = ageSec < 600 ? 'text-emerald-400' : 'text-amber-400';
  } else {
    ageLabel = `${Math.round(ageSec / 3600)}h ago`;
    ageTone = 'text-rose-400';
  }

  const todaysCount = summary.todays_decisions ?? 0;

  return (
    <div
      className="text-xs font-mono px-3 py-2 border border-border/60 bg-surface/40 flex items-center gap-x-4 gap-y-1 flex-wrap"
      data-testid="shadow-freshness-strip"
    >
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Activity className="w-3 h-3 text-accent" />
        Window:
      </span>
      <span className="text-foreground" data-testid="shadow-window-range">
        {todayOnly ? (
          <>
            <span className="font-semibold">Today</span>{' '}
            <span className="text-muted-foreground">({toDate} IST)</span>
          </>
        ) : (
          <>
            <span className="font-semibold">{fromDate}</span>
            <span className="mx-1 text-muted-foreground/60">→</span>
            <span className="font-semibold">{toDate}</span>
            <span className="ml-1 text-muted-foreground">IST</span>
          </>
        )}
      </span>

      <span className="text-muted-foreground/60">·</span>
      <span className="text-muted-foreground">
        Today&apos;s decisions:{' '}
        <span
          className={todaysCount > 0 ? 'text-emerald-400 font-semibold' : 'text-rose-400 font-semibold'}
          data-testid="shadow-todays-count"
        >
          {todaysCount}
        </span>
      </span>

      <span className="text-muted-foreground/60">·</span>
      <span className="text-muted-foreground">
        Latest decision:{' '}
        <span className={ageTone + ' font-semibold'} data-testid="shadow-latest-age">
          {ageLabel}
        </span>
        {latest && (
          <span className="ml-1 text-muted-foreground/70">
            ({latest.slice(11, 19)} IST)
          </span>
        )}
      </span>
    </div>
  );
};


const GUIHierarchyShadowTab = () => {
  const [days, setDays] = useState(5);
  const [todayOnly, setTodayOnly] = useState(false);
  const [symbol, setSymbol] = useState('');
  const [summary, setSummary] = useState(null);
  const [blockRows, setBlockRows] = useState([]);
  const [reasonRows, setReasonRows] = useState([]);
  const [pnlData, setPnlData] = useState(null);
  const [signalsData, setSignalsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ days: String(days) });
      if (todayOnly) qs.set('today_only', 'true');
      if (symbol) qs.set('symbol', symbol);

      const reasonQs = new URLSearchParams({
        days: String(days),
        group_by_symbol: 'true',
      });
      if (todayOnly) reasonQs.set('today_only', 'true');
      if (symbol) reasonQs.set('symbol', symbol);

      const [sum, blocks, reasons, pnl, signals] = await Promise.all([
        apiFetch(`/gui-hierarchy/shadow/summary?${qs.toString()}`),
        apiFetch(`/gui-hierarchy/shadow/blocks?${qs.toString()}&limit=50`),
        apiFetch(`/gui-hierarchy/shadow/counts-by-block-reason?${reasonQs.toString()}`),
        apiFetch(`/gui-hierarchy/shadow/pnl-summary?${qs.toString()}`),
        apiFetch(`/gui-hierarchy/shadow/hierarchy-signals?${qs.toString()}&dedup_window_min=15`),
      ]);
      setSummary(sum);
      setBlockRows(blocks.rows || []);
      setReasonRows(reasons.rows || []);
      setPnlData(pnl);
      setSignalsData(signals);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [days, todayOnly, symbol]);

  useEffect(() => {
    load();
  }, [load]);

  const matrixRows = useMemo(() => summary?.breakdown || [], [summary]);

  return (
    <div className="space-y-6" data-testid="gui-hierarchy-shadow-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <FlaskConical className="w-6 h-6 text-accent" />
            <h2 className="font-heading text-2xl font-bold">
              GUI Hierarchy — Shadow Mode
            </h2>
            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[10px]">
              PHASE 3a · OBSERVATIONAL
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            The video-aligned 4-layer hierarchy evaluates every live-engine tick
            silently. Use this view to measure its block rate on real fires
            before promoting any layer to active blocking.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="bg-surface border border-border px-3 py-2 text-sm font-mono"
            value={todayOnly ? 'today' : String(days)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'today') {
                setTodayOnly(true);
              } else {
                setTodayOnly(false);
                setDays(Number(v));
              }
            }}
            data-testid="shadow-days-select"
          >
            <option value="today">Today (IST)</option>
            {[1, 3, 5, 7, 14, 30].map((d) => (
              <option key={d} value={d}>
                Last {d}d
              </option>
            ))}
          </select>

          <select
            className="bg-surface border border-border px-3 py-2 text-sm font-mono"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            data-testid="shadow-symbol-select"
          >
            <option value="">All symbols</option>
            <option value="NIFTY">NIFTY</option>
            <option value="BANKNIFTY">BANKNIFTY</option>
          </select>

          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="shadow-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div
          className="flex items-start gap-2 p-3 border border-rose-500/40 bg-rose-500/10 text-rose-400 text-sm"
          data-testid="shadow-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-semibold">Failed to load shadow data</div>
            <div className="text-xs mt-0.5 font-mono">{error}</div>
          </div>
        </div>
      )}

      {/* Session 146 — explicit window + freshness strip. Proves whether
          today's ticks are included in the view the user is reading. */}
      <DataFreshnessStrip summary={summary} todayOnly={todayOnly} loading={loading} />

      {/* Cards */}
      <SummaryCards summary={summary} />

      {/* Hypothetical PnL — the "was the hierarchy right?" panel */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-accent" />
          <h3 className="font-heading font-semibold">
            Hypothetical PnL — was the hierarchy right?
          </h3>
          <span className="text-xs text-muted-foreground">
            — fills in t+5/15/30/60m after each decision
          </span>
        </div>
        <HypotheticalPnLPanel data={pnlData} />
      </div>

      {/* Hierarchy-would-have-taken trades (deduped) */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-accent" />
          <h3 className="font-heading font-semibold">
            Trades the hierarchy would have taken
          </h3>
          <span className="text-xs text-muted-foreground">
            — deduped into unique setups; one row = one trade if live
          </span>
        </div>
        <HierarchySignalsPanel data={signalsData} loading={loading} />
      </div>

      {/* Per-symbol × layer block matrix — answers "at what layer did
          each symbol's signals get blocked?" */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-accent" />
          <h3 className="font-heading font-semibold">
            Where each symbol's signals got blocked
          </h3>
          <span className="text-xs text-muted-foreground">
            — NIFTY vs BANKNIFTY × layer
          </span>
        </div>
        <PerSymbolLayerMatrix rows={reasonRows} />
      </div>

      {/* Agreement matrix + full-reason histogram side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-accent" />
            <h3 className="font-heading font-semibold">Agreement by symbol</h3>
          </div>
          <AgreementMatrix rows={matrixRows} />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-accent" />
            <h3 className="font-heading font-semibold">
              Block reasons (detailed)
            </h3>
          </div>
          <BlockReasonHistogram rows={reasonRows} />
        </div>
      </div>

      {/* LIVE_ONLY queue */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-accent" />
          <h3 className="font-heading font-semibold">
            LIVE_ONLY fires (live engine fired · hierarchy would block)
          </h3>
          <span className="text-xs text-muted-foreground">
            — the signals we're trying to shrink
          </span>
        </div>
        <LiveOnlyBlocksTable rows={blockRows} />
      </div>
    </div>
  );
};

export default GUIHierarchyShadowTab;
